import React from 'react';
// @ts-ignore
import DHTypography from "@digital-hig/mui/Typography";
// @ts-ignore
import DHBox from "@digital-hig/mui/Box";
// @ts-ignore
import DHCard from "@digital-hig/mui/Card";
// @ts-ignore
import DHButton from "@digital-hig/mui/Button";
import FileIconSVG from './FileIcon';

interface MainCardProps {
  title?: string;
  description?: string;
  buttonText?: string;
  onButtonClick?: () => void;
}

export const MainCard: React.FC<MainCardProps> = ({ 
  title = "Generate Configuration File",
  description = "Create a configuration file for dark site access. Ensure you gather the account details from Salesforce first.",
  buttonText = "Generate File",
  onButtonClick
}) => {
  return (
    <DHCard 
      elevation={0} 
      sx={{ 
        borderRadius: 1,
        backgroundColor: 'background.default',
        border: '1px solid var(--divider-0, #E5E5E5)',
        boxShadow: 'none',
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        justifyContent: 'space-between',
        // Digital HIG responsive dimensions
        width: { xs: '100%', sm: '100%', md: '100%', lg: '911px', xl: '911px' },
        height: '215px',
        minHeight: '215px',
        // Digital HIG breakpoint for 1320px
        '@media (min-width: 1320px)': {
          width: '911px',
          height: '215px',
          minHeight: '215px'
        },
        '@media (max-width: 1319px)': {
          width: '100%',
          height: '215px',
          minHeight: '215px'
        }
      }}
    >
      {/* Card Content */}
      <DHBox sx={{ 
        textAlign: 'left',
        flexShrink: 0,
        // Digital HIG responsive dimensions
        width: { xs: '100%', sm: '100%', md: '100%', lg: '863px', xl: '863px' },
        height: '151px',
        py: { xs: 2, sm: 2, md: 2, lg: 3, xl: 3 },
        pt: { xs: 2, sm: 2, md: 2, lg: 3, xl: 3 },
        px: { xs: 2, sm: 2, md: 2, lg: 0, xl: 0 },
        boxSizing: 'border-box',
        // Digital HIG breakpoint for 1320px
        '@media (min-width: 1320px)': {
          width: '863px',
          height: '151px',
          px: 0
        },
        '@media (max-width: 1319px)': {
          width: '100%',
          height: '151px',
          px: { xs: '16px', sm: '20px', md: '20px' }
        }
      }}>
        <DHTypography 
          variant="headline-small" 
          component="h3" 
          gutterBottom 
          sx={{
            fontFamily: 'Artifakt Legend',
            fontWeight: 800,
            // Digital HIG responsive font size
            fontSize: { xs: '20px', sm: '22px', md: '24px', lg: '26px', xl: '26px' },
            lineHeight: '120%',
            letterSpacing: '0%',
            // Digital HIG breakpoint for 1320px
            '@media (min-width: 1320px)': {
              fontSize: '26px',
              lineHeight: '120%'
            },
            '@media (max-width: 1319px)': {
              fontSize: { xs: '20px', sm: '22px', md: '24px' },
              lineHeight: '120%'
            }
          }}
        >
          {title}
        </DHTypography>
        <DHTypography 
          variant="caption" 
          color="ink-on-background-60" 
          sx={{
            fontFamily: 'Artifakt Element',
            fontWeight: 400,
            // Digital HIG responsive font size
            fontSize: { xs: '12px', sm: '13px', md: '14px', lg: '14px', xl: '14px' },
            lineHeight: '125%',
            height:'36px',
            letterSpacing: '0%',
            // Digital HIG breakpoint for 1320px
            '@media (min-width: 1320px)': {
              fontSize: '14px',
              lineHeight: '125%'
            },
            '@media (max-width: 1319px)': {
              fontSize: { xs: '12px', sm: '13px', md: '14px' },
              lineHeight: '125%'
            }
          }}
        >
          {description}
        </DHTypography>
      </DHBox>
    
      {/* Card Button */}
      <DHBox sx={{ 
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        flexShrink: 0,
        // Digital HIG responsive dimensions
        width: { xs: '100%', sm: '100%', md: '100%', lg: '911px', xl: '911px' },
        height: '64px',
        pl: { xs: 2, sm: 2, md: 2, lg: 7, xl: 7 },
        pr: { xs: 2, sm: 2, md: 2, lg: 0, xl: 0 },
        pb: { xs: 2, sm: 2, md: 2, lg: 0, xl: 0 },
        boxSizing: 'border-box',
        // Digital HIG breakpoint for 1320px
        '@media (min-width: 1320px)': {
          width: '911px',
          height: '64px',
          pl: 7,
          pr: 0,
          pb: 0
        },
        '@media (max-width: 1319px)': {
          width: '100%',
          height: '64px',
          pl: { xs: '16px', sm: '20px', md: '20px' },
          pr: { xs: '16px', sm: '20px', md: '20px' },
          pb: { xs: '16px', sm: '20px', md: '20px' }
        }
      }}>
        <DHButton 
          onClick={onButtonClick}
          sx={{ 
            // Digital HIG responsive dimensions
            width: { xs: '100%', sm: '100%', md: '100%', lg: '336px', xl: '336px' },
            height: { xs: '44px', sm: '44px', md: '44px', lg: '40px', xl: '40px' },
            minWidth: '80px',
            gap: '10px',
            opacity: 1,
            padding: { xs: '12px 20px', sm: '12px 20px', md: '12px 20px', lg: '8px 20px', xl: '8px 20px' },
            borderRadius: '4px',
            fontFamily: 'Artifakt Element, sans-serif',
            fontWeight: 600,
            fontSize: '16px',
            lineHeight: '20px',
            letterSpacing: '0%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // Digital HIG breakpoint for 1320px
            '@media (min-width: 1320px)': {
              width: '336px',
              height: '40px',
              padding: '8px 20px',
              fontSize: '16px'
            },
            '@media (max-width: 1319px)': {
              width: '100%',
              height: '40px',
              padding: '8px 20px',
              fontSize: '16px'
            },
            // Prevent text size and position changes on hover
            '&:hover': {
              fontSize: '16px',
              lineHeight: '20px',
              letterSpacing: '0%',
              fontWeight: 600,
              padding: '8px 20px',
              transform: 'none',
              textDecoration: 'none'
            }
          }} 
          startIcon={<FileIconSVG />}
        >
          {buttonText}
        </DHButton> 
      </DHBox>
    </DHCard>
  );
};

